import { ComputedSKUInfo, SKU, Product } from "gen/catalog/models_pb"
import { IntegrationVendor } from "gen/proto/integration_vendor/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { FractionalMoney, Money } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"
export interface CatalogProductBase {
  id: UUID
  name: string
  upc: string
  sku: string
  accountNumber: string
  productId?: UUID
}

export interface OrgillCatalogProduct extends CatalogProductBase {
  vendor: IntegrationVendor.ORGILL | IntegrationVendor.ORGILL_DEMO
  suggestedRetail: Money
  vp1Cost: FractionalMoney
  vp2Cost: FractionalMoney
  advCost: FractionalMoney
  orderingUoM: string
  retailUnitOfMeasure: string
  modelNumber: string
}

export interface TrueValueCatalogProduct extends CatalogProductBase {
  vendor: IntegrationVendor.TRUE_VALUE_CORP | IntegrationVendor.TRUE_VALUE_DEMO
  suggestedRetailPrice: Money
  variableRetailPrice: Money
  wholesaleVariableRetailPrice: Money
  wholesaleSuggestedRetail: Money
  orderPackSize: Decimal
  memberCostTier1: FractionalMoney
  tier1Quantity: Decimal
  memberCostTier2: FractionalMoney
  tier2Quantity: Decimal
  memberCostTier3: FractionalMoney
  tier3Quantity: Decimal
  memberCostTier4: FractionalMoney
  tier4Quantity: Decimal
  modelNumber: string
  weight: Decimal
  breakPack: string
  policyA: string
}

export interface DoItBestCatalogProduct extends CatalogProductBase {
  vendor: IntegrationVendor.DO_IT_BEST | IntegrationVendor.DO_IT_BEST_DEMO
  suggestedRetail: Money
  classic1Cost: FractionalMoney
  classic2Cost: FractionalMoney
  classic3Cost: FractionalMoney
  vision1Cost: FractionalMoney
  vision2Cost: FractionalMoney
  vision3Cost: FractionalMoney
  suggestedClassicMargin: Decimal
  suggestedVisionMargin: Decimal
  orderMultiples: Decimal[]
  retailUnitOfMeasure: string
  orderingUnitOfMeasure: string
  discontinuedStatus: string
  conversionFactor: Decimal
}

export interface EmeryJensenCatalogProduct extends CatalogProductBase {
  vendor: IntegrationVendor.EMERY_JENSEN | IntegrationVendor.EMERY_JENSEN_DEMO
  aceRetailAmount: Money
  regularRetailAmount: Money
  retailStorePrice: Money
  brandName: string
  vendorNumber: string
  productGroupCode: string
  centralWarehouseCode: string
  replenishmentStockNumber: string
  orderMultipleQuantity: Decimal
  sellingUom: string
  itemStatusCode: string
}

export interface BlishMizeCatalogProduct extends CatalogProductBase {
  vendor: IntegrationVendor.BLISH_MIZE
  dealerRetail: Money
  sellPack: Decimal
  breakSellPack: string
  costUnit: string
  retailUnit: string
  stockingUnitOfMeasure: string
  retailUnitConversion: Decimal
  vendorName: string
  mfgPartNumber: string
  fineLineClass: string
  retailDepartment: string
  weight: Decimal
}

export interface HouseHassonCatalogProduct extends CatalogProductBase {
  vendor: IntegrationVendor.HOUSE_HASSON
  shipUnit: string
  stockPack: Decimal
  retailPack: Decimal
  department: string
  weight: Decimal
  doNotBreakFlag: string
  suggestedRetailPrice: Money
  cost: FractionalMoney
  discontinued: boolean
}

export interface WallaceCatalogProduct extends CatalogProductBase {
  vendor: IntegrationVendor.WALLACE
  mfgVendorCost: FractionalMoney
  replacementCost: FractionalMoney
  retailPrice: FractionalMoney
  standardPackage: number
  stockingUom: string
  purchaseUom: string
  mfgVendor: string
  mfgPartNumber: string
  department: string
  class: string
  fineline: string
  itemWeight: string
  gtin: string
}

export type CatalogProduct =
  | OrgillCatalogProduct
  | TrueValueCatalogProduct
  | DoItBestCatalogProduct
  | EmeryJensenCatalogProduct
  | BlishMizeCatalogProduct
  | HouseHassonCatalogProduct
  | WallaceCatalogProduct

export interface CatalogProductDisplayField {
  label: string
  value: string
}

type VendorFieldKeys<T extends CatalogProductBase> = Exclude<
  keyof T,
  keyof CatalogProductBase
>

export type VendorDisplayFields<
  T extends CatalogProductBase,
  THidden extends VendorFieldKeys<T> = never,
> = Record<Exclude<VendorFieldKeys<T>, THidden>, CatalogProductDisplayField>

export const IntegrationVendorLabelMap: Record<IntegrationVendor, string> = {
  [IntegrationVendor.UNSPECIFIED]: "Unknown",
  [IntegrationVendor.BENJAMIN_MOORE]: "Benjamin Moore",
  [IntegrationVendor.BENJAMIN_MOORE_TEST]: "Benjamin Moore Test",
  [IntegrationVendor.BENJAMIN_MOORE_DEMO]: "Benjamin Moore Demo",
  [IntegrationVendor.TRUE_VALUE_CORP]: "True Value",
  [IntegrationVendor.TRUE_VALUE_DEMO]: "True Value Demo",
  [IntegrationVendor.ALL_PRO]: "All Pro",
  [IntegrationVendor.ALL_PRO_DEMO]: "All Pro Demo",
  [IntegrationVendor.EMERY_JENSEN]: "Emery Jensen",
  [IntegrationVendor.EMERY_JENSEN_DEMO]: "Emery Jensen Demo",
  [IntegrationVendor.ORGILL]: "Orgill",
  [IntegrationVendor.ORGILL_DEMO]: "Orgill Demo",
  [IntegrationVendor.ORGILL_RDC]: "Orgill RDC",
  [IntegrationVendor.DO_IT_BEST]: "Do It Best",
  [IntegrationVendor.DO_IT_BEST_DEMO]: "Do It Best Demo",
  [IntegrationVendor.HOUSE_HASSON]: "House Hasson",
  [IntegrationVendor.BUTTERY]: "Buttery",
  [IntegrationVendor.WALLACE]: "Wallace",
  [IntegrationVendor.LANCASTER]: "Lancaster",
  [IntegrationVendor.PITTS_PAINTS]: "Pitts Paints",
  [IntegrationVendor.PACOA]: "Pacoa",
  [IntegrationVendor.BLISH_MIZE]: "Blish Mize",
  [IntegrationVendor.FLORIDA_HARDWARE]: "Florida Hardware",
  [IntegrationVendor.LASCO]: "Lasco",
  [IntegrationVendor.INVALID]: "Invalid",
}

export interface SkuProductParams {
  sku?: SKU
  computedInfo?: ComputedSKUInfo
  catalogProduct?: Product
  linkedProductIdentifier?: string
  linkedProductId?: PBUUID
}

export interface SelectedProductData {
  sku: SKU
  computedInfo: ComputedSKUInfo
  catalogProduct: Product
  linkedProductIdentifier: string
  linkedProductId?: UUID
}
